// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { ProfilingButton } from '@shared/advisory/account-workflow/components';
import type { AdvisoryProposalContractResponseBody } from '@shared/data-access/interfaces/api/brokerage/client-types';
import { Table } from '@shared/ui-kit';
import { Translation } from '@shared/ui-kit/providers';
import { formatDate } from '@shared/ui-kit/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './silver-alpha-home.module.css';

export interface SilverAlphaPendingProposalCardProps {
  proposal: AdvisoryProposalContractResponseBody;
}

export const SilverAlphaPendingProposalCard: FC<
  SilverAlphaPendingProposalCardProps
> = ({ proposal }) => {
  const { t, i18n } = useTranslation();

  const perimeter = useMemo(
    () =>
      (proposal.institutions ?? [])
        .map(institution => institution.name?.trim())
        .filter(Boolean)
        .join(', '),
    [proposal.institutions],
  );

  const meta = [
    formatDate(proposal.created_at, 'standard', i18n.language),
    proposal.advisor?.name,
  ]
    .filter(Boolean)
    .join(' · ');

  const contractType = t(
    `activation-step2.advisory-types.${proposal.advisory_type}.label`,
    { ns: 'components' },
  );

  const statusLabelKey =
    proposal.status === 'active'
      ? 'common.active'
      : proposal.status === 'inactive'
      ? 'common.inactive'
      : 'common.pending';
  const showOnboardingCta = proposal.status === 'pending';

  return (
    <article
      className={styles.platinumCard}
      data-testid={`silver-alpha-pending-card-${proposal.id}`}
    >
      <div className={styles.platinumHeader}>
        <p className={styles.platinumEyebrow}>
          <Translation
            i18nKey="silver-alpha-home.proposals-title"
            ns="components"
          />
        </p>
        <span
          className={styles.statusPill}
          data-testid={`advisory-proposal-status-${proposal.id}`}
        >
          <span className={styles.statusDot} aria-hidden />
          {t(statusLabelKey, { ns: 'common' })}
        </span>
      </div>

      <p className={styles.platinumMeta}>{meta}</p>

      <dl className={styles.platinumGrid}>
        <div className={styles.platinumField}>
          <dt className={styles.platinumLabel}>
            <Translation
              i18nKey="advisory-proposal.contract-type"
              ns="components"
            />
          </dt>
          <dd className={styles.platinumValue}>{contractType}</dd>
        </div>
        <div className={styles.platinumField}>
          <dt className={styles.platinumLabel}>
            {t('common.commission', { ns: 'common' })}
          </dt>
          <dd className={styles.platinumValue}>
            <span className="inline-flex flex-wrap items-baseline gap-1">
              <Table.Data
                value={proposal.commission_managed_assets}
                format="percentage"
                formatOptions={{
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }}
              />
              <span>
                {t('advisory-proposal.commission-suffix', { ns: 'components' })}
              </span>
            </span>
          </dd>
        </div>
        {perimeter ? (
          <div className={styles.platinumField}>
            <dt className={styles.platinumLabel}>
              <Translation i18nKey="advisory-proposal.perimeter" ns="components" />
            </dt>
            <dd className={styles.platinumValue}>{perimeter}</dd>
          </div>
        ) : null}
      </dl>

      <div className={styles.platinumFooter}>
        <p className={styles.platinumIssuer}>
          <Translation i18nKey="advisory-proposal.issuer" ns="components" />
        </p>
        {showOnboardingCta ? (
          <div className={styles.cta}>
            <ProfilingButton proposal={proposal} />
          </div>
        ) : null}
      </div>
    </article>
  );
};
